import React, { useState, useEffect } from "react";
import "./css/card.css";
import "./css/table.css";
import "./css/form.css";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import {
  Select as ShadSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Users,
  UserCheck,
  Package,
  Settings,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Check,
  X,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useGetRepairRequestQuery,
  useAcceptOrRejectRepairRequestMutation,
  useUpdateRepairByIdMutation,
} from "../../slices/maintenanceApiSlice";
import {
  useGetUserByEmailQuery,
  useGetUsersQuery,
} from "../../slices/userApiSlice";
import { useGetAssetQuery } from "../../slices/assetApiSlice";

const priorities = [
  { value: "Immediate", label: "Immediate (Within 24 hours)" },
  { value: "High", label: "High (Within 1-2 days)" },
  { value: "Moderate", label: "Moderate (Within 1 week)" },
  { value: "Low", label: "Low (More than a week)" },
];
const ManagerDashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [acceptConfirm, setAcceptConfirm] = useState({
    open: false,
    repairId: null,
  });
  const [rejectConfirm, setRejectConfirm] = useState({
    open: false,
    repairId: null,
  });
  const [updatePriorityConfirm, setUpdatePriorityConfirm] = useState({
    open: false,
    data: null,
  });
  const rowsPerPage = 10;

  const { data: repairRequest, refetch: refetchRepairRequest } =
    useGetRepairRequestQuery();

  const [acceptRepairRequest, { isLoading: isLoadingA }] =
    useAcceptOrRejectRepairRequestMutation();
  const [rejectRepairRequest, { isLoading: isLoadingB }] =
    useAcceptOrRejectRepairRequestMutation();

  const selectUserInfo = (state) => state.auth.userInfo || {};

  const getUserEmail = createSelector(selectUserInfo, (userInfo) => {
    const email = userInfo?.username || userInfo?.email || "";
    return email === "null" ? "" : email;
  });
  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email, {
    skip: !email, // Skip query if email is empty
  });

  const academyId = userByEmial?.user?.academyId;

  const { data: users, isLoading: usersLoading } = useGetUsersQuery();
  const { data: asset } = useGetAssetQuery();

  const filteredUsers = users?.filter(
    (user) =>
      user.academyId === academyId &&
      typeof user.role?.name === "string" &&
      user.role.name.toLowerCase() === "technician",
  );

  const totalRepair = repairRequest?.filter(
    (req) => req.academyId === academyId && req.accept === true,
  );

  const SupervisorsUsers = users?.filter(
    (user) =>
      user.academyId === academyId &&
      typeof user.role?.name === "string" &&
      user.role.name.toLowerCase() === "supervisor",
  );

  const assets = asset?.filter((ass) => ass.academyID === academyId);

  const [data, setData] = useState([]);

  const [isEditingPriority, setIsEditingPriority] = useState(false);
  const [editablePriority, setEditablePriority] = useState(null);

  const [updateRepairById, { isLoading: isUpdating }] =
    useUpdateRepairByIdMutation();

  useEffect(() => {
    if (modalData) {
      setEditablePriority(
        priorities.find((p) => p.value === modalData.priority) || null,
      );
      setIsEditingPriority(false); // reset edit mode
    }
  }, [modalData]);

  useEffect(() => {
    const userAcademy = userByEmial?.user?.academyId?.trim().toLowerCase();

    if (!userAcademy || !repairRequest) return;

    const filtered = repairRequest.filter((req) => {
      const requestAcademy = req.academyId?.trim().toLowerCase();
      return requestAcademy === userAcademy && req.accept === null;
    });

    const sortedFiltered = filtered.sort((a, b) => {
      if (!a.repairID || !b.repairID) return 0;
      return b.repairID.localeCompare(a.repairID);
    });

    setData(sortedFiltered);
  }, [repairRequest, userByEmial]);

  // Sorting
  const [sortOrder, setSortOrder] = useState({ column: null, ascending: true });

  const sortData = (column, ascending) => {
    const sortedData = [...data].sort((a, b) => {
      if (a[column] < b[column]) return ascending ? -1 : 1;
      if (a[column] > b[column]) return ascending ? 1 : -1;
      return 0;
    });
    setData(sortedData);
  };

  const handleAccept = async (repairId, acceptValue) => {
    setAcceptConfirm({ open: true, repairId, acceptValue });
  };

  const confirmAccept = async () => {
    toast.promise(
      async () => {
        await acceptRepairRequest({
          repairId: acceptConfirm.repairId,
          accept: acceptConfirm.acceptValue,
        }).unwrap();
        handleCloseModal();
        refetchRepairRequest();
        setAcceptConfirm({ open: false, repairId: null, acceptValue: null });
      },
      {
        loading: "Sending Mail...",
        success: "Mail sent successfully to the user.",
        error: (err) => {
          console.error("Error:", err);
          return "Update Failed: Unable to update the repair request at this time.";
        },
      },
    );
  };

  const handleReject = async (repairId) => {
    setRejectConfirm({ open: true, repairId });
  };

  const confirmReject = async () => {
    toast.promise(
      async () => {
        await rejectRepairRequest({
          repairId: rejectConfirm.repairId,
          accept: false,
        }).unwrap();
        handleCloseModal();
        refetchRepairRequest();
        setRejectConfirm({ open: false, repairId: null });
      },
      {
        loading: "Sending Mail...",
        success: "Mail sent successfully to the user.",
        error: (err) => {
          console.error("Error:", err);
          return "Update Failed: Unable to update the repair request at this time.";
        },
      },
    );
  };

  const confirmUpdatePriority = async () => {
    try {
      await updateRepairById({
        repairID: updatePriorityConfirm.data.repairID,
        updateFields: {
          priority: updatePriorityConfirm.data.priority.value,
        },
      }).unwrap();

      toast.success("Priority updated successfully.");
      setIsEditingPriority(false);
      refetchRepairRequest();
      setUpdatePriorityConfirm({ open: false, data: null });
    } catch (err) {
      toast.error("Failed to update priority.");
      console.error(err);
    }
  };

  const handleSort = (column) => {
    const newSortOrder =
      column === sortOrder.column
        ? !sortOrder.ascending // Toggle the sorting direction if the same column is clicked
        : true; // Start with ascending for a new column

    setSortOrder({
      column,
      ascending: newSortOrder,
    });
    sortData(column, newSortOrder);
  };

  // Extract unique priorities from data
  const uniquePriorities = [
    { value: "all", label: "All Priorities" },
    ...Array.from(
      new Set(
        data.map((item) => item.priority?.toLowerCase()).filter(Boolean), // remove undefined/null
      ),
    ).map((priority) => ({
      value: priority,
      label: priority.charAt(0).toUpperCase() + priority.slice(1), // Capitalize first letter
    })),
  ];

  // const sortedData = [...data].sort((a, b) => b.rid - a.rid);

  const filteredData = data.filter((item) => {
    const matchesSearch = Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );
    const matchesPriority =
      selectedPriority === "" ||
      selectedPriority === "all" ||
      item.priority?.toLowerCase() === selectedPriority.toLowerCase();

    return matchesSearch && matchesPriority;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleDeleteSelected = () => {
    const updatedData = data.filter(
      (item) => !selectedRows.includes(item.repairID),
    );
    // Update the data with the filtered result after deletion
    setData(updatedData);
    setSelectedRows([]); // Reset selected rows after deletion
  };

  const handleView = (item) => {
    setModalData(item);
  };
  const handleCloseModal = () => {
    setModalData(null);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getFormattedDate = () => {
    const today = new Date();
    const options = { weekday: "long", month: "long", day: "numeric" };
    return today.toLocaleDateString("en-US", options);
  };

  const greeting = getGreeting();
  const formattedDate = getFormattedDate();
  const name = userByEmial?.user?.name || "User";
  const userProfileImage = userByEmial?.user?.image || null;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const statsCards = [
    {
      title: "Total Technicians",
      value: filteredUsers?.length || 0,
      icon: Users,
      description: "Active technicians in academy",
      color: "bg-blue-500",
      lightColor: "bg-blue-50",
      textColor: "text-blue-600",
    },
    {
      title: "Total Supervisors",
      value: SupervisorsUsers?.length || 0,
      icon: UserCheck,
      description: "Active supervisors in academy",
      color: "bg-amber-500",
      lightColor: "bg-amber-50",
      textColor: "text-amber-600",
    },
    {
      title: "Total Assets",
      value: assets?.length || 0,
      icon: Package,
      description: "Assets under management",
      color: "bg-green-500",
      lightColor: "bg-green-50",
      textColor: "text-green-600",
    },
    {
      title: "Repair Requests",
      value: totalRepair?.length || 0,
      icon: Settings,
      description: "Total repair requests",
      color: "bg-red-500",
      lightColor: "bg-red-50",
      textColor: "text-red-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/50">
      <div className="p-4 sm:py-6 sm:px-8 space-y-6">
        {/* Welcome Header Card */}
        <Card className="border-none shadow-lg bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-white/20 ring-4 ring-white/10">
                  <AvatarImage src={userProfileImage} alt={name} />
                  <AvatarFallback className="bg-white/10 text-white text-lg font-semibold">
                    {getInitials(name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2 text-sm text-white/70 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formattedDate}</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold">
                    {greeting}, {name}!
                  </h1>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="hover:shadow-lg transition-all duration-300 border-none shadow-md hover:-translate-y-1"
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.lightColor} p-2.5 rounded-lg`}>
                    <Icon className={`h-5 w-5 ${stat.textColor}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Latest Request Table */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">Latest Requests</CardTitle>
                <CardDescription className="mt-1">
                  Manage and review repair requests
                </CardDescription>
              </div>
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-4">
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="w-full sm:w-64">
                <ShadSelect
                  value={selectedPriority}
                  onValueChange={(value) => setSelectedPriority(value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniquePriorities.map((priority) => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </ShadSelect>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sl. No.</TableHead>
                    <TableHead>Asset Name</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Area</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No repair requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedData.map((item, index) => (
                      <TableRow key={item.id || index}>
                        <TableCell className="font-medium">
                          {(currentPage - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>{item.assetName || "N/A"}</TableCell>
                        <TableCell>{item.name || "N/A"}</TableCell>
                        <TableCell>{item.phoneNumber || "N/A"}</TableCell>
                        <TableCell>{item.area || "N/A"}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {item.description || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(item)}
                            style={{
                              backgroundColor:
                                "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                              color: "white",
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {filteredData.length}{" "}
                {filteredData.length === 1 ? "Result" : "Results"}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className="w-8"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                {totalPages > 5 && (
                  <>
                    <span className="text-muted-foreground">...</span>
                    <Button
                      variant={
                        currentPage === totalPages ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      className="w-8"
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modal for Viewing Request */}
        <Dialog
          open={!!modalData}
          onOpenChange={(open) => !open && handleCloseModal()}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="pb-3 border-b">
              <DialogTitle className="text-xl font-semibold">
                Repair Request Details
              </DialogTitle>
            </DialogHeader>
            {modalData && (
              <div className="overflow-y-auto flex-1 py-4 space-y-4">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-500 uppercase">
                        Name
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                        {modalData.name}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-500 uppercase">
                        Phone Number
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                        {modalData.phoneNumber}
                      </div>
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase">
                        Email
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                        {modalData.email}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Asset Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Asset Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-500 uppercase">
                        Asset Name
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                        {modalData.assetName}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-500 uppercase">
                        Area
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                        {modalData.area}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Priority Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Priority
                  </h3>
                  {!isEditingPriority ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                        {editablePriority?.label || "Not Set"}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingPriority(true)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <ShadSelect
                        value={editablePriority?.value || ""}
                        onValueChange={(value) => {
                          const selected = priorities.find(
                            (p) => p.value === value,
                          );
                          setEditablePriority(selected || null);
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          {priorities.map((priority) => (
                            <SelectItem
                              key={priority.value}
                              value={priority.value}
                            >
                              {priority.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </ShadSelect>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            if (editablePriority) {
                              setUpdatePriorityConfirm({
                                open: true,
                                data: {
                                  repairID: modalData.repairID,
                                  priority: editablePriority,
                                },
                              });
                            }
                          }}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Confirm
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditablePriority(
                              priorities.find(
                                (p) => p.value === modalData.priority,
                              ) || null,
                            );
                            setIsEditingPriority(false);
                          }}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Description
                  </h3>
                  <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 leading-relaxed min-h-[80px]">
                    {modalData.description}
                  </div>
                </div>

                {/* Images */}
                {modalData.images && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Repair Images
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Array.isArray(modalData.images) ? (
                        modalData.images.map((imgSrc, index) => (
                          <div
                            key={index}
                            className="relative aspect-square rounded-md overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <img
                              src={imgSrc}
                              alt={`Work Order ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))
                      ) : (
                        <div className="relative aspect-square rounded-md overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors">
                          <img
                            src={modalData.images}
                            alt="Work Order"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    className="flex-1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAccept(modalData.repairID, true);
                    }}
                    disabled={isLoadingA}
                    style={{
                      backgroundColor:
                        "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                      color: "white",
                    }}
                  >
                    {isLoadingA ? "Accepting..." : "Accept Request"}
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleReject(modalData.repairID);
                    }}
                    disabled={isLoadingB}
                  >
                    {isLoadingB ? "Rejecting..." : "Reject Request"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Accept Repair Confirmation Dialog */}
        <AlertDialog
          open={acceptConfirm.open}
          onOpenChange={(open) =>
            !open &&
            setAcceptConfirm({ open: false, repairId: null, acceptValue: null })
          }
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Accept Repair Request?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to accept this repair request? A
                confirmation email will be sent to the user.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() =>
                  setAcceptConfirm({
                    open: false,
                    repairId: null,
                    acceptValue: null,
                  })
                }
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmAccept}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                Accept
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reject Repair Confirmation Dialog */}
        <AlertDialog
          open={rejectConfirm.open}
          onOpenChange={(open) =>
            !open && setRejectConfirm({ open: false, repairId: null })
          }
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reject Repair Request?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to reject this repair request? A rejection
                email will be sent to the user.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() =>
                  setRejectConfirm({ open: false, repairId: null })
                }
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmReject}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Reject
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Update Priority Confirmation Dialog */}
        <AlertDialog
          open={updatePriorityConfirm.open}
          onOpenChange={(open) =>
            !open && setUpdatePriorityConfirm({ open: false, data: null })
          }
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Update Priority?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to update the priority to "
                {updatePriorityConfirm.data?.priority?.label}"?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() =>
                  setUpdatePriorityConfirm({ open: false, data: null })
                }
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmUpdatePriority}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Update
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default ManagerDashboard;
